/**
 * Fonte única dos price IDs do Stripe.
 *
 * Antes eles viviam copiados em quatro arquivos — Pricing, Plans, Signup e
 * UserSettings — e as cópias divergiram: três telas ficaram com uma geração
 * antiga que o backend não reconhece mais, e o checkout respondia
 * `400 Unknown price` a partir delas.
 *
 * Os valores abaixo são o padrão; para trocar um preço sem alterar código,
 * defina a variável correspondente. No Create React App as variáveis precisam
 * do prefixo REACT_APP_ e são embutidas no bundle em tempo de build — trocá-las
 * exige rebuild, não basta reiniciar.
 *
 * Price ID não é segredo: identifica um preço público no Stripe.
 */
const ler = (nome, padrao) => process.env[nome] || padrao;

export const PRICE_IDS = {
  monthly: {
    Basic: ler('REACT_APP_STRIPE_PRICE_BASIC_MONTHLY', 'price_1RYun92NzYztBUJG1KMoet55'),
    Guru: ler('REACT_APP_STRIPE_PRICE_GURU_MONTHLY', 'price_1RYuoD2NzYztBUJGxB78tOrn'),
    Pro: ler('REACT_APP_STRIPE_PRICE_PRO_MONTHLY', 'price_1U0MEH2NzYztBUJGlCBWPNuB')
  },
  annual: {
    Basic: ler('REACT_APP_STRIPE_PRICE_BASIC_ANNUAL', 'price_1RYunT2NzYztBUJGhqiGppXI'),
    Guru: ler('REACT_APP_STRIPE_PRICE_GURU_ANNUAL', 'price_1RYuoW2NzYztBUJGQesUo054'),
    Pro: ler('REACT_APP_STRIPE_PRICE_PRO_ANNUAL', 'price_1U0MEH2NzYztBUJGUEigETwb')
  }
};

/**
 * Os valores cobrados, ao lado dos IDs que os cobram.
 *
 * Existiam três conjuntos de preço no produto, e nenhum batia com outro:
 * a home dizia Guru $29 / Pro $49, a tela de planos dizia Guru $19 / Pro $39, e
 * o Stripe cobrava Guru $29 / Pro $79. Quem clicava em Pro na home via $49 e
 * chegava num checkout de $79; quem clicava dentro do app via $39.
 *
 * A causa não foi descuido em nenhuma das telas: o preço foi reajustado no
 * Stripe e não havia lugar único de onde as telas lessem. Enquanto o número for
 * digitado em cada tela, o próximo reajuste repete isto.
 *
 * Conferidos contra a API do Stripe em 19/08/2026. `anual` é o total cobrado
 * uma vez por ano, não o valor mensal — dividir é trabalho de quem exibe.
 *
 * O passo seguinte, que fecha a classe do problema, é ler estes valores do
 * próprio Stripe (`prices.list`, em cache) e apagar esta tabela.
 */
export const PRECOS = {
  Basic: { mensal: 0, anual: 0 },
  Guru: { mensal: 29, anual: 290 },
  Pro: { mensal: 79, anual: 790 }
};

/**
 * Quanto o ciclo anual economiza.
 *
 * Os dois planos pagos cobram exatamente dez meses por doze, ou seja 16,7%. A
 * tela dizia "Save 20%", que era mais um número que ninguém tinha conferido.
 * "2 months free" descreve o mesmo desconto sem arredondar nada.
 */
/* Derivado dos preços, e não digitado: mudar um valor acima atualiza o selo
   sozinho, então ele não tem como voltar a divergir da cobrança.

   O menor desconto entre os planos pagos, e não o do Pro: se um dia as
   proporções diferirem, o selo promete o que vale para todos em vez de exagerar
   para quem tiver o desconto menor. */
export const MESES_GRATIS_NO_ANUAL = Math.min(
  ...Object.values(PRECOS)
    .filter((p) => p.mensal > 0)
    .map((p) => 12 - p.anual / p.mensal)
);

/** Valor mensal equivalente do plano anual, para comparação na tabela. */
export const mensalEquivalente = (plano) => PRECOS[plano].anual / 12;

/**
 * Pacotes avulsos de credito — pagamento unico, nao assinatura.
 *
 * O checkout destes precisa rodar em `mode: 'payment'`, e o webhook precisa
 * somar creditos ao saldo em vez de trocar o plano. Enquanto isso nao existir
 * no backend, estes IDs ficam aqui apenas registrados.
 */
export const CREDIT_PACKS = [
  { creditos: 25, preco: 16, priceId: 'price_1U0MEJ2NzYztBUJG9hQlMZYJ' },
  { creditos: 50, preco: 30, priceId: 'price_1U0MEJ2NzYztBUJGuyR3yb3E' },
  { creditos: 100, preco: 57, priceId: 'price_1U0MEJ2NzYztBUJG2I7cXbVe' },
  { creditos: 200, preco: 110, priceId: 'price_1U0MEJ2NzYztBUJG7QMvfFyX' }
];

/**
 * Resolve o price ID de um plano.
 *
 * ⚠️ O backend valida o ID recebido contra o próprio mapa e recusa o que não
 * conhecer. Hoje ele conhece apenas os três mensais — assinatura anual precisa
 * que os IDs correspondentes sejam adicionados a `config/stripe.js` no backend,
 * senão o checkout responde `400 Unknown price`.
 */
export const priceIdFor = (plano, ciclo = 'monthly') => {
  const doCiclo = PRICE_IDS[ciclo];
  return (doCiclo && doCiclo[plano]) || null;
};

export default PRICE_IDS;
